import moment from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import type { DateTimeFilter } from '../../../types/components/filter_bar_types';

interface SpaceWithUnavailableTimeSlots {
  unavailableTimeSlots: {
    date: Dayjs;
    startOffset: number;
    endOffset: number;
  }[];
  venue: {
    hoursOfOperation: {
      dayOfWeek: string;
      isOpen: boolean;
      startTime: number;
      endTime: number;
    }[];
    expressBookSettings: {
      minLeadTimeDays: number;
      maxLeadTimeDays: number;
      minEventDurationHours: number;
      maxEventDurationHours: number;
      minBufferTimeHours: number;
    };
  };
}
export const checkDateTimeFilter = (
  space: SpaceWithUnavailableTimeSlots,
  dateTimeFilter: DateTimeFilter | null,
): boolean => {
  if (!dateTimeFilter) {
    return true;
  }

  const { date, startOffset, endOffset } = dateTimeFilter;

  if (!date && !startOffset && !endOffset) {
    return true;
  }
  if (startOffset && endOffset) {
    const requestedEventDuration =
      (endOffset - startOffset) / 60;

    const isTooShort =
      requestedEventDuration <
      space.venue.expressBookSettings.minEventDurationHours;

    const isTooLong =
      requestedEventDuration >
      space.venue.expressBookSettings.maxEventDurationHours;

    if (isTooShort || isTooLong) {
      return false;
    }
  }

  const isOpen = checkHoursOfOperation(space, dateTimeFilter);

  if (!isOpen) {
    return false;
  }

  if (!startOffset && !endOffset) {
    return true;
  }

  const maxLeadTime = space.venue.expressBookSettings.maxLeadTimeDays;
  const maxLeadTimeDate = moment().add(maxLeadTime, 'days');
  const isWithinMaxLeadTime = date?.isBefore(
    maxLeadTimeDate.toDate(),
  );

  if (date && !isWithinMaxLeadTime) {
    return false;
  }

  const minLeadTime = space.venue.expressBookSettings.minLeadTimeDays;
  const minLeadTimeDate = moment().add(minLeadTime, 'days');
  const isWithinMinLeadTime = date?.isAfter(
    minLeadTimeDate.toDate(),
  );

  if (date && !isWithinMinLeadTime) {
    return false;
  }

  const relevantUnavailableTimeSlots = space.unavailableTimeSlots.filter((t) =>
    dayjs(t.date).isSame(dateTimeFilter?.date?.toDate(), 'day'),
  );

  const minBufferTimeMinutes =
    space.venue.expressBookSettings.minBufferTimeHours * 60;

  const isBlocked = !!relevantUnavailableTimeSlots.some((booking) => {
    return overlaps(booking, dateTimeFilter, minBufferTimeMinutes);
  });

  if (isBlocked) {
    return false;
  }

  return true;
};

const checkHoursOfOperation = (
  space: SpaceWithUnavailableTimeSlots,
  dateTimeFilter: DateTimeFilter,
) => {
  const { date, startOffset, endOffset } = dateTimeFilter;
  if (!date && startOffset && endOffset) {
    // check if the space is open at that time on any day of the week
    const isOpenThatTimeOnAnyDay = space.venue.hoursOfOperation.some(
      (venueAvailabilityDay) =>
        venueAvailabilityDay.isOpen &&
        venueAvailabilityDay.startTime <= startOffset &&
        venueAvailabilityDay.endTime >= endOffset,
    );
    return isOpenThatTimeOnAnyDay;
  }
  if (date) {
    const relevantHoursOfOperation = space.venue.hoursOfOperation.find(
      (venueAvailabilityDay) =>
        date?.format('dddd').toLowerCase() ===
        venueAvailabilityDay.dayOfWeek,
    );
    if (!relevantHoursOfOperation) {
      return false;
    }
    if (!startOffset || !endOffset) {
      return relevantHoursOfOperation.isOpen;
    }
    return relevantHoursOfOperation.isOpen &&
      relevantHoursOfOperation.startTime <= startOffset &&
      relevantHoursOfOperation.endTime >= endOffset;
    
  }
  return false;
};

const overlaps = (
  booking: {
    date: Dayjs;
    startOffset: number;
    endOffset: number;
  },
  dateTimeFilter: DateTimeFilter,
  minBufferTimeMinutes: number,
) => {
  const { date, startOffset, endOffset } = dateTimeFilter;
  if (!startOffset || !endOffset) {
    return false;
  }

  const isSameDay = dayjs(booking.date).isSame(date, 'day');

  const isBefore =
    booking.endOffset + minBufferTimeMinutes < startOffset;
  const isAfter =
    booking.startOffset - minBufferTimeMinutes > endOffset;

  const isOverlapping = !isBefore && !isAfter;

  return isSameDay && isOverlapping;
};
